import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import { orderlineDisplayName } from '../../../../../services/orderlines/orderlines.service';
import type { ProductView } from '../../../../../services/products/products.service';
import type { CategoryView } from '../../../../../services/categories/categories.service';
import type { AreaPrintRule } from 'shared-models/browser';
import type { KdsPrintTicketLine } from '../../../../KdsPage/components/print/KdsPrintTicket';
import {
  splitOrderlineSelections,
  groupOrderlineSelections,
  type ComboSelectionRoute,
} from '../../../../../utils/orderline-selections';

export type KitchenPrintLogItem = { name: string; quantity: number; isKds: boolean };

export type KitchenPrintGroup = {
  printerId: number;
  groupLines: KdsPrintTicketLine[];
  logItems: KitchenPrintLogItem[];
  hasKdsLines: boolean;
  orderlineIds: number[];
};

export type KitchenPrintGroups = {
  groups: KitchenPrintGroup[];
  // Routable (printer or KDS) line count; a KDS-only line is deliverable but forms no group.
  deliverableCount: number;
  // Orderline ids the server will flip to 'sent'; drives the optimistic post-send patch.
  deliverableLineIds: number[];
  undeliverableNames: string[];
  // Lines that reach KDS but whose mapped printer has no active target — bon is NOT printed.
  kdsBonMissingNames: string[];
  unroutedComboWarning: { fatherName: string; childName: string } | null;
};

type ResolveComboRoute = (productId: number, itemId: number) => ComboSelectionRoute | null;
type PrintableLine = { line: OrderlineView; comboItem?: ReturnType<typeof splitOrderlineSelections>['comboItems'][number] };

function stripTrailingComboQty(name?: string | null): string {
  if (!name) return '';
  const withoutDirectionMarks = name.replace(/[\u200E\u200F\u202A-\u202E]/g, '').trim();
  return withoutDirectionMarks.replace(/(?:\s*[x×]\s*\d+\s*)+$/iu, '').trim();
}

// Groups pending lines by resolved printer into KDS ticket + print-log payloads (name-independent).
export function buildKitchenPrintGroups(params: {
  lines: OrderlineView[];
  posProducts: ProductView[];
  categories?: CategoryView[];
  resolveComboSelectionRoute?: ResolveComboRoute;
  activePrinterIds?: number[];
  areaId?: number | null;
  areaPrintRules?: AreaPrintRule[];
}): KitchenPrintGroups {
  const { lines, posProducts, categories = [], resolveComboSelectionRoute, activePrinterIds, areaId, areaPrintRules } = params;

  // An inactive/disabled printer is not a print target (mirrors backend active+enabled routing).
  const activeSet = activePrinterIds ? new Set(activePrinterIds) : null;
  const activeOnly = (ids: number[]): number[] => (activeSet ? ids.filter((id) => activeSet.has(id)) : ids);

  const areaRulePrinterByProductId = new Map<number, number>();
  if (areaId != null && areaPrintRules && areaPrintRules.length > 0) {
    for (const rule of areaPrintRules) {
      const mapping = rule.printerMappings.find((m) => m.areaIds.includes(areaId));
      if (mapping) {
        for (const productId of rule.productIds) {
          areaRulePrinterByProductId.set(productId, mapping.printerId);
        }
      }
    }
  }

  const resolveComboItemPrinters = (comboItem: ReturnType<typeof splitOrderlineSelections>['comboItems'][number]): number[] => {
    if (comboItem.areaPrintRuleId && areaId != null && areaPrintRules && areaPrintRules.length > 0) {
      const explicitRule = areaPrintRules.find((rule) => rule.id === comboItem.areaPrintRuleId);
      const explicitMapping = explicitRule?.printerMappings.find((mapping) => mapping.areaIds.includes(areaId));
      if (explicitMapping) {
        return activeOnly([explicitMapping.printerId]);
      }
    }

    const rawChildName = stripTrailingComboQty(comboItem.name);
    const matchingProduct = posProducts.find((p) => p.name.trim() === rawChildName);
    if (matchingProduct && areaRulePrinterByProductId.has(matchingProduct.id)) {
      const rulePrinterId = areaRulePrinterByProductId.get(matchingProduct.id)!;
      return activeOnly([rulePrinterId]);
    }
    return activeOnly(comboItem.printerIds ?? []);
  };

  const productToPrinters = new Map<number, number[]>();
  const productCategory = new Map(posProducts.map((product) => [product.id, product.categoryId ?? null]));
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const productKdsMap = new Map<number, boolean>();
  const productHasPrinterMapping = new Set<number>();
  for (const product of posProducts) {
    productKdsMap.set(product.id, product.kds === true);
    if (product.printerIds.length > 0) {
      productHasPrinterMapping.add(product.id);
    }
    const rulePrinterId = areaRulePrinterByProductId.get(product.id);
    const activeProductPrinters = rulePrinterId != null ? activeOnly([rulePrinterId]) : activeOnly(product.printerIds);
    if (activeProductPrinters.length > 0) {
      productToPrinters.set(product.id, activeProductPrinters);
    }
  }

  const unroutedComboEntry = lines
    .map((line) => {
      const { comboItems } = splitOrderlineSelections(line, resolveComboSelectionRoute);
      const unroutedChild = comboItems.find((item) => resolveComboItemPrinters(item).length === 0 && item.kds !== true);
      return unroutedChild ? { line, childName: stripTrailingComboQty(unroutedChild.name) } : null;
    })
    .find((entry): entry is { line: OrderlineView; childName: string } => entry !== null);

  const unroutedComboWarning = unroutedComboEntry
    ? { fatherName: unroutedComboEntry.line.productName ?? 'מוצר', childName: unroutedComboEntry.childName }
    : null;

  const undeliverableLines = lines.filter((line) => {
    if (splitOrderlineSelections(line, resolveComboSelectionRoute).comboItems.length > 0) return false;
    if (line.productId == null) return false;
    return !productToPrinters.has(line.productId) && productKdsMap.get(line.productId) !== true;
  });

  // KDS delivers but the mapped printer has no active target: the send is real, the bon is not.
  const kdsBonMissingLines = lines.filter((line) => {
    if (splitOrderlineSelections(line, resolveComboSelectionRoute).comboItems.length > 0) return false;
    if (line.productId == null) return false;
    return productHasPrinterMapping.has(line.productId)
      && !productToPrinters.has(line.productId)
      && productKdsMap.get(line.productId) === true;
  });

  const deliverableLines = lines.filter((line) => {
    const { comboItems } = splitOrderlineSelections(line, resolveComboSelectionRoute);
    if (comboItems.length > 0) return comboItems.every((item) => resolveComboItemPrinters(item).length > 0 || item.kds === true);
    if (line.productId == null) return true;
    return productToPrinters.has(line.productId) || productKdsMap.get(line.productId) === true;
  });

  const linesByPrinter = new Map<string, { printerId: number; lines: PrintableLine[] }>();
  const separateTicketCategoryId = (categoryId: number | null): number | null => {
    const visited = new Set<number>();
    let currentCategoryId = categoryId;
    while (currentCategoryId != null && !visited.has(currentCategoryId)) {
      visited.add(currentCategoryId);
      const category = categoriesById.get(currentCategoryId);
      if (!category) return null;
      if (category.separateKitchenBon) return category.id;
      currentCategoryId = category.parentCategoryPk;
    }
    return null;
  };
  const addToPrinterGroup = (printerId: number, printable: PrintableLine, categoryId: number | null) => {
    const separateCategoryId = separateTicketCategoryId(categoryId);
    const key = `${printerId}:${separateCategoryId != null ? `category-${separateCategoryId}` : 'shared'}`;
    const group = linesByPrinter.get(key) ?? { printerId, lines: [] };
    group.lines.push(printable);
    linesByPrinter.set(key, group);
  };
  for (const line of deliverableLines) {
    const { comboItems } = splitOrderlineSelections(line, resolveComboSelectionRoute);
    if (comboItems.length > 0) {
      for (const comboItem of comboItems) {
        for (const printerId of resolveComboItemPrinters(comboItem)) {
          addToPrinterGroup(printerId, { line, comboItem }, null);
        }
      }
      continue;
    }
    const printerIds = line.productId != null ? (productToPrinters.get(line.productId) ?? []) : [];
    if (printerIds.length === 0) continue;
    for (const printerId of printerIds) {
      addToPrinterGroup(printerId, { line }, line.productId != null ? (productCategory.get(line.productId) ?? null) : null);
    }
  }

  const groups: KitchenPrintGroup[] = [];
  for (const { printerId, lines: bucket } of linesByPrinter.values()) {
    const groupLines: KdsPrintTicketLine[] = bucket.map(({ line, comboItem }, index) => {
      if (comboItem) {
        return {
          id: line.id * 1000 + index + 1,
          productName: stripTrailingComboQty(comboItem.name),
          selectionGroups: groupOrderlineSelections(comboItem.nestedSelections),
          quantity: line.quantity * Math.max(1, comboItem.quantity),
          note: index === 0 && line.note && line.note.trim().length > 0 ? line.note.trim() : null,
        };
      }
      const lineNote = line.note && line.note.trim().length > 0 ? line.note.trim() : null;
      return {
        id: line.id,
        productName: orderlineDisplayName(line),
        selectionGroups: groupOrderlineSelections(line.selections),
        quantity: line.quantity,
        note: lineNote,
      };
    });

    const logItems: KitchenPrintLogItem[] = bucket.map(({ line, comboItem }) => {
      const isKds = comboItem ? comboItem.kds === true : line.productId != null && productKdsMap.get(line.productId) === true;
      if (comboItem) {
        const nestedLabels = comboItem.nestedSelections.map((selection) => selection.itemName);
        const name = nestedLabels.length > 0
          ? `${stripTrailingComboQty(comboItem.name)} (${nestedLabels.join(', ')})`
          : stripTrailingComboQty(comboItem.name);
        return { name, quantity: line.quantity * Math.max(1, comboItem.quantity), isKds };
      }
      const { variantLabels } = splitOrderlineSelections(line, resolveComboSelectionRoute);
      const name = variantLabels.length > 0 ? `${orderlineDisplayName(line)} (${variantLabels.join(', ')})` : orderlineDisplayName(line);
      return { name, quantity: line.quantity, isKds };
    });

    const hasKdsLines = logItems.some((item) => item.isKds);
    const orderlineIds = Array.from(new Set(bucket.map(({ line }) => line.id)));
    groups.push({ printerId, groupLines, logItems, hasKdsLines, orderlineIds });
  }

  return {
    groups,
    deliverableCount: deliverableLines.length,
    deliverableLineIds: Array.from(new Set(deliverableLines.map((line) => line.id))),
    undeliverableNames: undeliverableLines.map((line) => orderlineDisplayName(line)),
    kdsBonMissingNames: kdsBonMissingLines.map((line) => orderlineDisplayName(line)),
    unroutedComboWarning,
  };
}

// Applies the resolved printer name to a group's log payload (route labels + KDS suffix).
export function resolveKitchenGroupLabels(
  group: KitchenPrintGroup,
  printerName: string,
): { logPrinterName: string; logItems: Array<{ name: string; quantity: number; routeLabel: string }> } {
  const logItems = group.logItems.map((item) => ({
    name: item.name,
    quantity: item.quantity,
    routeLabel: item.isKds ? `${printerName}, KDS` : printerName,
  }));
  const logPrinterName = group.hasKdsLines ? `${printerName}, KDS` : printerName;
  return { logPrinterName, logItems };
}
